import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { NavLink, useParams } from "react-router";
import { useRelationshipsRelationshipShowQuery } from "@/services/backend/relationships/api/service";
import { Commitment } from "@/services/backend/relationships/types/relationship";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { CpyNameForDocument } from "@/services/backend/cpy/lib/cpy-name-for-document";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { formatDate, weekRFC3339 } from "@/shared/lib/utilities/date";

export function CommitmentRoute() {
  const { relationshipId, commitmentId } = useParams();

  const {
    data: relationship,
    error,
    isLoading,
  } = useRelationshipsRelationshipShowQuery({
    id: relationshipId!,
  });

  const commitment = relationship?.commitments.find(
    (c) => c.id === commitmentId,
  );

  const breadcrumbs = relationship && commitment && (
    <VbsBreadcrumbs
      customerId={relationship.customerId}
      processId={commitment.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/vbs/${commitment.processId!}/commitments`}>
            {t("Sollstellungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          {formatDate(commitment.date) +
            " (KW " +
            weekRFC3339(commitment.date) +
            ")"}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {!isLoading && !error && !commitment && (
        <RTKQueryErrorAlert error={{ status: 404 }} />
      )}
      {commitment && <CommitmentPage commitment={commitment} />}
    </ProcessPageMainTemplate>
  );
}

function CommitmentPage({ commitment }: { commitment: Commitment }) {
  const links = [
    { to: "data", label: t("Daten") },
    { to: "pricing", label: t("Preise") },
    { to: "calculation", label: t("Kalkulation") },
    { to: "treatment", label: t("Behandlung") },
  ];

  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <DocumentPageNavigation>
              {links.map((link) => (
                <DocumentPageNavigationLink key={link.to} to={link.to}>
                  {link.label}
                </DocumentPageNavigationLink>
              ))}
            </DocumentPageNavigation>
            <CpyDialog
              destinationEntityName={CpyNameForDocument(
                GSMServiceKind.VBS,
                commitment.documentKind!,
              )}
              destinationId={commitment.documentId!}
            />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <DocumentPageNavigationOutlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}
